import React from 'react'

import { At as PositionedText, Block, Sheet } from './_shared/positioned'

// Réplica do modelo "Fumigation Certificate / Certificado de Fumigação" da Biofix (A4), com as coordenadas em pt
// do modelo. Os textos que podem crescer (remetente, demais exportadores e local) empurram o resto para baixo.
const FONT = "Arial, Helvetica, 'Liberation Sans', sans-serif"
const SIZE = 5.9
const THIN = '0.6pt solid #000'

// Texto posicionado, com a fonte pequena do modelo como padrão
const At = ({ size = SIZE, ...props }) => <PositionedText size={size} {...props} />

// Rótulo "INGLÊS / português :" (inglês em negrito, português em itálico)
const Label = ({ y, x = 38.6, en, pt, colon = true }) => (
  <At x={x} y={y}>
    <b>{en} / </b>
    <i>{pt}</i>
    {colon && <b> :</b>}
  </At>
)

// Valor com `x` à esquerda, ou centralizado numa caixa de largura `w` quando informada
const Value = ({ x, y, w, size = SIZE, bold, children }) => (
  <At x={x} y={y} w={w} size={size} bold={bold} align={w ? 'center' : undefined}>
    {children}
  </At>
)

const Slash = ({ x, y }) => (
  <At x={x} y={y}>
    /
  </At>
)

// Mesmo valor em inglês (negrito) e em português (normal), separados por "/"
const Bilingual = ({ y, en, pt, enCenter, ptCenter, slash, w = 110, size }) => (
  <>
    <Value x={enCenter - w / 2} y={y} w={w} size={size} bold>
      {en}
    </Value>
    <Slash x={slash} y={y} />
    <Value x={ptCenter - w / 2} y={y} w={w} size={size}>
      {pt}
    </Value>
  </>
)

// Reduz a fonte quando o texto é longo, para continuar legível sem estourar a linha
const fit = (text, [limit1, limit2], [normal, medium, small]) =>
  text.length <= limit1 ? normal : text.length <= limit2 ? medium : small

const Top = ({ data }) => (
  <Block top={0} height={389.6} plain>
    <img
      src="/pdfs/biofix/logo.png"
      alt="Biofix Agri"
      style={{ position: 'absolute', left: '172.8pt', top: '67pt', width: '182.8pt', height: '50.7pt' }}
    />
    <At x={38.6} y={135.3} w={464.8} size={7.8} bold italic align="center">
      FUMIGATION CERTIFICATE
    </At>
    <At x={38.6} y={144} w={464.8} size={7.8} italic align="center">
      CERTIFICADO DE FUMIGAÇÃO
    </At>
    {[
      ['BR  SP 0896', '', 154.6],
      ['CNPJ:', '31.707.506/0001-69', 164.1],
      ['CREA-SP', '2180493', 173.6],
      ['O.S. Nº BR -', data.activityCode, 183.3],
      ['Nº Comunicado', data.communicationNumber, 192.8],
      ['Nº Certificado', data.communicationNumber, 202.3]
    ].map(([label, value, y]) => (
      <React.Fragment key={label}>
        <At x={38.6} y={y} bold>
          {label}
        </At>
        <At x={86.3} y={y} bold>
          {value}
        </At>
      </React.Fragment>
    ))}
    <At x={38.6} y={211.8} bold>
      LPCO:
    </At>
    <At x={86.3} y={211.4} w={420} bold wrap style={{ lineHeight: '6.6pt' }}>
      {data.lpco}
    </At>
    <At x={38.6} y={228.9} w={415.6} size={6.5} bold align="center" wrap style={{ lineHeight: '7.5pt' }}>
      THE UNDERSIGNED, CERTIFIES THAT THE PRODUCTS REPORTED OF THE UNDER DESCRIBED DESPATCH WERE SUBMITTED TO FUMIGATION
      ACCORDINGLY FOLLOWING INDICATIONS:
    </At>
    <At x={38.6} y={243.9} w={415.6} size={6.5} italic align="center" wrap style={{ lineHeight: '7.5pt' }}>
      O ABAIXO ASSINADO, CERTIFICA QUE OS PRODUTOS CONSTANTES DA REMESSA ABAIXO DESCRITA FORAM SUBMETIDOS A FUMIGAÇÃO
      CONFORME INDICAÇÃO A SEGUIR:
    </At>
    <At x={38.6} y={280.7} w={466} size={6.5} bold align="center">
      DESCRIPTION OF THE CONSIGNMENT
    </At>
    <At x={38.6} y={291} w={466} size={6.5} italic align="center">
      DESCRIÇÃO DA REMESSA
    </At>

    <Label y={313.3} en="LOT NUMBER" pt="NÚMERO LOTE" />
    <Label y={326} en="ORIGIN" pt="ORIGEM" />
    <Value x={150} y={326.1} w={140} bold>
      {data.port} , BRAZIL
    </Value>
    <Slash x={269.8} y={326.4} />
    <Value x={285} y={326.2} w={142}>
      {data.port} , BRASIL
    </Value>
    <Label y={338.7} en="MARCA/LABEL" pt="MARCA" />
    <At x={202} y={338.6} bold>
      -
    </At>
    <Label y={351.5} en="QUANTITY OF PACKAGES" pt="QUANTIDADE DE VOLUMES" />
    <At x={265.1} y={351.3} bold>
      -
    </At>
    <Label y={364.2} en="KIND OF PRODUCT" pt="NATUREZA DO PRODUTO" />
    <Bilingual y={364.1} en={data.goodsEn} pt={data.goodsPt} enCenter={295.2} ptCenter={450.2} slash={377.9} />
    <Label y={377.4} en="GROSS WHEIGHT" pt="PESO BRUTO" />
    <At x={259.5} y={377.3} bold>
      MT
    </At>
    <Slash x={292.7} y={377.3} />
    <At x={420.9} y={377.3}>
      TM
    </At>
  </Block>
)

const Shipper = ({ data }) => (
  <Block top={389.6} minHeight={28.9} plain>
    <Label x={41.3} y={389.6} en="NAME AND ADDRESS OF SHIPPER" pt="NOME E ENDEREÇO DO REMETENTE" />
    <div style={{ marginLeft: '255pt', width: '255pt', textAlign: 'center', fontWeight: 'bold' }}>
      <div style={{ fontSize: `${SIZE}pt`, lineHeight: '9.7pt' }}>{data.shipper}</div>
      <div
        style={{
          fontSize: `${fit(data.shipperDetails, [190, 300], [5.2, 4.6, 4.2])}pt`,
          lineHeight: '6.8pt'
        }}
      >
        {data.shipperDetails}
      </div>
    </div>
  </Block>
)

const OtherShippers = ({ data }) => {
  const size = fit(data.otherShippers, [110, 240], [SIZE, 5, 4.4])
  return (
    <Block top={418.5} minHeight={16.2} plain>
      <Label y={418.5} en="OTHER SHIPPERS" pt="DEMAIS EXPORTADORES" />
      <div
        style={{
          marginLeft: '180pt',
          width: '340pt',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: `${size}pt`,
          lineHeight: `${size + 0.7}pt`
        }}
      >
        {data.otherShippers}
      </div>
    </Block>
  )
}

const Treatment = ({ data }) => (
  <Block top={434.7} height={66.2} plain>
    <Label y={434.7} en="NAME AND ADDRESS OF CONSIGNEE" pt="NOME E ENDEREÇO DO DESTINATÁRIO" />
    <Value x={359} y={434.6} w={70}>
      TO ORDER
    </Value>
    <Label y={447.9} en="DESTINATION" pt="DESTINO" />
    <Bilingual
      y={447.3}
      en={data.destinationEn}
      pt={data.destinationPt}
      enCenter={187.5}
      ptCenter={267.7}
      slash={242.3}
      w={60}
    />
    <At x={38.6} y={469.1} w={465} size={6.5} align="center">
      <b>TREATMENT - </b>
      <i>TRATAMENTO</i>
    </At>
    <Label y={488.1} en="FUMIGATION AT" pt="FUMIGADO EM" />
    <Value x={142.5} y={487.5} w={90} bold>
      {data.ship}
    </Value>
    <Value x={226} y={487.8} w={80} size={5.2} bold>
      {data.port}
    </Value>
    <At x={308.2} y={488} bold>
      PORT
    </At>
    <Slash x={333.1} y={488} />
    <Value x={335} y={487.5} w={90}>
      {data.ship}
    </Value>
    <At x={420.9} y={488}>
      , PORTO DE
    </At>
    <At x={469.8} y={488}>
      {data.port}
    </At>
  </Block>
)

const Place = ({ data }) => (
  <Block top={500.9} minHeight={25.4} plain>
    <Label y={500.9} en="PLACE" pt="LOCAL" />
    <Value x={132.7} y={500.8} w={60}>
      {data.place}
    </Value>
    <At x={193.8} y={500.8}>
      BERTH
    </At>
    <At x={242.2} y={500.8}>
      -
    </At>
    <div
      style={{
        marginLeft: '259.5pt',
        width: '245pt',
        paddingTop: '2.2pt',
        fontSize: `${fit(data.placeAddress, [150, 260], [SIZE, 5.2, 4.6])}pt`,
        lineHeight: '7.2pt'
      }}
    >
      {data.placeAddress}
    </div>
  </Block>
)

const Details = ({ data }) => (
  <>
    <Label y={526.3} en="FUMIGATION PRODUCT" pt="PRODUTO UTILIZADO" />
    <At x={203} y={525.7} bold>
      ALUMINIUM PHOSPHIDE
    </At>
    <Value x={271.2} y={525.7} w={80} bold>
      {data.product}
    </Value>
    <Slash x={340.5} y={526.2} />
    <At x={372.1} y={525.7}>
      FOSFETO DE ALUMINIO
    </At>
    <Value x={437.3} y={525.7} w={80}>
      {data.product}
    </Value>

    <At x={38.6} y={539.1}>
      <b>QUANTITY OF PRODUCT USED </b> / QUANTIDADE DE PRODUTO UTILIZADA
    </At>
    <Value x={265.2} y={538.5} w={80} bold>
      {data.grams}
    </Value>
    <At x={329.1} y={538.9} bold>
      GRAMS
    </At>
    <Slash x={363.8} y={538.9} />
    <Value x={381.9} y={538.5} w={80}>
      {data.grams}
    </Value>
    <At x={449.9} y={538.9}>
      GRAMAS
    </At>

    <Label y={551.8} en="DOSING" pt="DOSAGEM" />
    <At x={187.9} y={551.2} bold>
      {data.dosageEn}
    </At>
    <Slash x={292.7} y={551.7} />
    <At x={357.9} y={551.2}>
      {data.dosagePt}
    </At>

    <Label y={564.5} en="EXPOSITION TIME" pt="TEMPO DE EXPOSIÇÃO" />
    <At x={216.2} y={564.4} bold>
      {data.hours && `${data.hours} HRS`}
    </At>
    <At x={277.6} y={564.4} bold>
      {data.days && `${data.days} DAYS`}
    </At>
    <Slash x={350.5} y={564.4} />
    <At x={383.2} y={564.4} bold>
      {data.hours && `${data.hours} HS`}
    </At>
    <At x={444} y={564.4} bold>
      {data.days && `${data.days} DIAS`}
    </At>

    <Label y={577.2} en="DATE OF FUMIGATION" pt="DATA DA FUMIGAÇÃO" />
    <At x={193.8} y={576.6} bold>
      {data.dateEn}
    </At>
    <Slash x={350.5} y={577.1} />
    <Value x={384} y={576.6} w={100}>
      {data.datePt}
    </Value>

    <Label y={589.9} en="START TIME" pt="HORA INÍCIO" />
    <Slash x={209.4} y={589.8} />
    <At x={259.5} y={589.9}>
      <b>CONCLUSION</b> / TÉRMINO:
    </At>
    <Slash x={406.5} y={589.8} />

    <Label y={602.7} en="TEMPERATURE" pt="TEMPERATURA" />
    <At x={193.8} y={602.5} bold>
      º C
    </At>

    <At x={38.6} y={615.4}>
      <b>METHOD / </b>MÉTODO :
    </At>
    <Bilingual y={614.8} en={data.methodEn} pt={data.methodPt} enCenter={215} ptCenter={404.3} slash={292.7} w={120} />

    <At x={38.6} y={627.6}>
      <b>FUMIGATED HOLDS </b>/ PORÕES FUMIGADOS
    </At>
    <Bilingual y={627} en={data.holds} pt={data.holds} enCenter={248.8} ptCenter={417} slash={305.4} w={100} />

    <At x={38.6} y={650.7}>
      <b>REMARKS / </b>
      <i>OBSERVAÇÕES :</i>
    </At>
  </>
)

const Signatures = ({ data }) => (
  <>
    <At x={395.5} y={688.3} size={4.6}>
      {data.port}
    </At>
    <At x={463.5} y={687.3}>
      <span style={{ whiteSpace: 'pre' }}>{'          /            /'}</span>
    </At>
    {[
      [38.8, 94.9, 'COMANDANTE DA EMBARCAÇÃO', 'VESSEL CAPTAIN'],
      [148.3, 95, 'STAMP AUTHORITY OFFICIAL', 'CARIMBO DO ÓRGÃO OFICIAL'],
      [261, 100, 'FUMIGATION COMPANY', 'FUMIGADORA'],
      [395.7, 118.8, 'PLACE AND DATE', 'LOCAL E DATA']
    ].map(([x, w, title, subtitle]) => (
      <React.Fragment key={title}>
        <At x={x} y={701.3} w={w} style={{ borderTop: THIN, height: 0 }} />
        <At x={x} y={706} w={w} size={5.2} bold align="center">
          {title}
        </At>
        <At x={x} y={715.3} w={w} size={5.2} italic={subtitle !== 'VESSEL CAPTAIN'} align="center">
          {subtitle}
        </At>
      </React.Fragment>
    ))}
  </>
)

const Footer = () => {
  const style = { fontFamily: "Calibri, Carlito, 'Liberation Sans', sans-serif" }
  return (
    <>
      <At x={51.3} y={743.2} size={7.8} bold style={style}>
        Biofix Agri LTDA | Avenida Afonso Pena 511 sala 45 – Estuário – Santos/SP
      </At>
      <At x={51.3} y={752.8} size={7.8} bold style={style}>
        T: +55 14 3731-1483 | E: biofix@biofixagri.com | www.biofixagri.com
      </At>
      <div
        style={{
          position: 'absolute',
          left: '330.1pt',
          top: `${727.5 - 526.3}pt`,
          height: '43.3pt',
          borderLeft: THIN,
          transform: 'rotate(6.2deg)',
          transformOrigin: 'top left'
        }}
      />
      <img
        src="/pdfs/biofix/gafta.png"
        alt="Gafta"
        style={{ position: 'absolute', left: '357.5pt', top: `${733.7 - 526.3}pt`, width: '33.1pt', height: '30.7pt' }}
      />
      <img
        src="/pdfs/biofix/fosfa.png"
        alt="FOSFA International"
        style={{ position: 'absolute', left: '409.5pt', top: `${732.8 - 526.3}pt`, width: '62.4pt', height: '33.1pt' }}
      />
    </>
  )
}

const CertificateDocument = ({ data }) => (
  <div id="pdfPage" style={{ width: '595.2pt', background: '#fff', fontFamily: FONT, color: '#000' }}>
    <style>{`@page { size: A4; margin: 0 }`}</style>
    <Sheet left={0} style={{ fontSize: `${SIZE}pt` }}>
      <Top data={data} />
      <Shipper data={data} />
      <OtherShippers data={data} />
      <Treatment data={data} />
      <Place data={data} />
      <Block top={526.3} height={260} plain>
        <Details data={data} />
        <Signatures data={data} />
        <Footer />
      </Block>
    </Sheet>
  </div>
)

export default CertificateDocument
